import { useRef } from 'react';
import styles from './ui.module.css';
import { Icon, type IconName } from './icon';
import { SiteChrome } from './chrome';

export { styles as ui };
export { SiteChrome, type SiteChromeProps } from './chrome';

/**
 * A measurement and what it measures.
 *
 * The value is always Martian Mono with tabular figures (section 7.2), and
 * there is deliberately no "muted" or "small" variant: unflattering numbers
 * get the same type, size and weight as flattering ones (hard rule 14).
 */
export function Stat({ value, label }: { value: string; label: string }): React.ReactElement {
  return (
    <div className={styles.stat}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

export function Rule(): React.ReactElement {
  return <hr className={styles.rule} />;
}

/**
 * The standard page shell: header, content, footer.
 *
 * A flex column with the main region growing, so the footer sits at the bottom
 * of the viewport on a short page rather than floating in the middle of it,
 * which reads as a rendering fault.
 *
 * **There is no `wide` prop any more.** It used to choose between a 72rem
 * reading measure and a 100rem grid measure; every page now runs the full window
 * (`--gb-measure`, in the token layer) with a gutter that grows with it, so
 * there was nothing left for the flag to pick between. Line length is still
 * held, per element, by `.gb-body` and `.gb-lead` — which is where it belongs,
 * because it is a property of a paragraph and not of a page.
 *
 * `bleed` renders edge to edge, between the navigation and the padded content.
 * A hero constrained to a text column is the single clearest tell that a games
 * site was built as a business site.
 */
export function Page({
  children,
  bleed,
  backdrop,
  surface = false,
  mark = true,
}: {
  children: React.ReactNode;
  bleed?: React.ReactNode;
  backdrop?: React.ReactNode;
  surface?: boolean;
  mark?: boolean;
}): React.ReactElement {
  const here = typeof window === 'undefined' ? undefined : window.location.pathname;

  return (
    <SiteChrome currentPath={here} bleed={bleed} backdrop={backdrop} surface={surface} mark={mark}>
      {children}
    </SiteChrome>
  );
}

/** A heading and a paragraph, used across the editorial pages. */
export function Point({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={styles.point}>
      <h3 className="gb-subhead">{title}</h3>
      <p className={`gb-body ${styles.pointBody}`}>{children}</p>
    </div>
  );
}

/**
 * One line of a money breakdown.
 *
 * `note` is where a figure says where it came from — the rate a tax was taken
 * at, the formula a fee was modelled with. Section 06 and hard rule 11: a
 * figure that cannot be traced back to something is a figure somebody has to
 * take on trust, and this is a screen where a studio is deciding what to
 * charge and who to share it with.
 */
export interface BreakdownRow {
  label: string;
  /** Formatted, by the caller. This component does no arithmetic and no money. */
  value: string;
  note?: string;
  /** A subtotal: ruled off above, and set in type colour rather than muted. */
  total?: boolean;
}

/**
 * A column of figures that add up, with a caption saying where they come from.
 *
 * Built for the submission flow's earnings simulation, where a studio picking a
 * creator rate needs to see the whole chain — what the buyer paid, what the tax
 * and the processing took, and how the three shares of what is left fall out —
 * rather than the one number at the end of it.
 *
 * **The caption is not optional and that is deliberate.** Every figure this
 * draws is either measured or modelled, and a column of money with nothing
 * saying which is a column somebody will quote back at us. It is the same
 * argument `formatShareWithDenominator` makes about percentages, applied to the
 * one other place the product puts arithmetic in front of a person.
 *
 * No colour. The shares here are not statuses and a green one would be reading
 * as good news — see the palette rules; a panel has one accent and this is not
 * what it is for.
 */
export function Breakdown({
  rows,
  caption,
}: {
  rows: readonly BreakdownRow[];
  caption: string;
}): React.ReactElement {
  return (
    <div className={styles.breakdown}>
      <dl className={styles.breakdownRows}>
        {rows.map((row) => (
          <div
            key={row.label}
            className={styles.breakdownRow}
            data-total={row.total === true ? 'true' : undefined}
          >
            <dt className={styles.breakdownLabel}>
              {row.label}
              {row.note && <span className={styles.breakdownNote}>{row.note}</span>}
            </dt>
            <dd className={`${styles.breakdownValue} ${styles.numeric}`}>{row.value}</dd>
          </div>
        ))}
      </dl>
      <p className={styles.breakdownCaption}>{caption}</p>
    </div>
  );
}

/**
 * Empty states are invitations to act, never apologies. Section 09.
 *
 * *"You have no games yet"* becomes *"Nothing installed. Go to the store, or
 * type in a key."* — say what is there and what to do, with no sad face and no
 * sorry. The picture above the sentence is the friendly half; the sentence
 * itself stays plain, which is section 09's division of labour exactly.
 *
 * `icon` defaults to the empty box rather than being required, so every empty
 * state in the product gets one without forty call sites having to choose.
 */
export function Empty({
  children,
  icon = 'box',
}: {
  children: React.ReactNode;
  icon?: IconName;
}): React.ReactElement {
  return (
    <div className={styles.empty}>
      <Icon name={icon} size={40} />
      <p className="gb-body">{children}</p>
    </div>
  );
}

/** One filter, and how many are behind it. */
export interface FilterOption {
  /** What the caller stores when this one is chosen. Never shown. */
  value: string;
  label: string;
  /** Omitted rather than zero when there is no number to give. */
  count?: number;
}

/**
 * A row of filters over one region, with the current one marked.
 *
 * **A component rather than a stylesheet entry, because the accessible half is
 * the half that gets skipped.** The admin review queue had this as a
 * `display: flex` typed inline around `.tag` buttons carrying `aria-pressed` —
 * see the note on `.filters` for why the tags were the wrong furniture — and a
 * row of toggle buttons is not what this is. Exactly one of these is chosen at
 * a time and choosing one changes what is shown below it, which is the tab
 * pattern, and the tab pattern is a `tablist` with arrow-key traversal rather
 * than seven independently tabbable buttons.
 *
 * **Automatic activation**, which the WAI-ARIA pattern reserves for tabs whose
 * panels are already loaded — filtering an array the browser is holding costs
 * nothing, and manual activation would mean arrowing along a row that says
 * nothing until you press space. So the arrow keys move the selection with the
 * focus, and the roving `tabIndex` keeps the whole row one stop rather than
 * seven on the way to the content.
 *
 * `controls` is the id of the region below. Optional only because a caller may
 * genuinely not have one element to point at; passing it is what lets a screen
 * reader move from the tab to what it filtered.
 */
export function Filters({
  label,
  options,
  value,
  onChange,
  controls,
}: {
  /** What is being filtered. Announced on the row, never drawn. */
  label: string;
  options: readonly FilterOption[];
  value: string;
  onChange: (value: string) => void;
  /** The id of the region these filter, for `aria-controls`. */
  controls?: string;
}): React.ReactElement {
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  function traverse(event: React.KeyboardEvent, from: number): void {
    const last = options.length - 1;
    const to =
      event.key === 'ArrowRight'
        ? from === last
          ? 0
          : from + 1
        : event.key === 'ArrowLeft'
          ? from === 0
            ? last
            : from - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : -1;

    const next = to < 0 ? undefined : options[to];
    if (!next) return;

    // The row wraps, so the arrow keys must not also scroll the page.
    event.preventDefault();
    onChange(next.value);
    tabs.current[to]?.focus();
  }

  return (
    <div className={styles.filters} role="tablist" aria-label={label}>
      {options.map((option, index) => {
        const current = option.value === value;

        return (
          <button
            key={option.value}
            ref={(element) => {
              tabs.current[index] = element;
            }}
            type="button"
            role="tab"
            aria-selected={current}
            {...(controls ? { 'aria-controls': controls } : {})}
            tabIndex={current ? 0 : -1}
            className={`${styles.filter ?? ''} ${current ? (styles.filterCurrent ?? '') : ''}`}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => traverse(event, index)}
          >
            {option.label}
            {option.count === undefined ? null : (
              <span className={styles.filterCount ?? ''}>{option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
